import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtDate, fmtDateTime, fmtAUD, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Pill, TEMP_STYLES, STATUS_STYLES } from "@/components/badges";
import TransferModal from "@/components/TransferModal";
import { ChevronLeft, ArrowRight, History, FileText, MessageSquare, CheckSquare, Trash2, ArrowLeftRight, Activity as ActivityIcon } from "lucide-react";
import { toast } from "sonner";
import ActivityTab from "@/components/ActivityTab";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TABS = [
  { id: "overview", label: "Overview", icon: FileText },
  { id: "activity", label: "Activity", icon: ActivityIcon },
  { id: "notes", label: "Notes", icon: MessageSquare },
  { id: "audit", label: "Audit Trail", icon: History },
  { id: "tasks", label: "Tasks", icon: CheckSquare },
];

const SOURCES = ["Self Generated", "Bark", "Firmable", "Partner", "Website", "Referral", "Networking Event", "Social Media", "Other"];
const TEMPS = ["Cold", "Warm", "Hot"];
const STATUSES = ["New", "Contacted", "Qualified", "Unqualified", "Converted"];
const STAGES = ["Need Assessment", "Formal Presentation", "Decision Pending", "Closed Won", "Closed Lost"];

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  const [partnersInCategory, setPartnersInCategory] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [tab, setTab] = useState("overview");
  const [notes, setNotes] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [showConvert, setShowConvert] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [pending, setPending] = useState({});
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === "admin";

  const reload = async () => {
    const [l, u, sc, p, c] = await Promise.all([api.get(`/leads/${id}`), api.get("/users"), api.get("/service-categories"), api.get("/partnerships"), api.get("/campaigns")]);
    setLead(l.data); setUsers(u.data); setServices(sc.data); setPartnerships(p.data); setCampaigns(c.data);
  };

  useEffect(() => { reload().catch(() => toast.error("Lead not found")); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (!lead?.partnership) { setPartnersInCategory([]); return; }
    api.get(`/partners?category=${encodeURIComponent(lead.partnership)}`).then((r) => setPartnersInCategory(r.data));
  }, [lead?.partnership]);
  useEffect(() => {
    if (tab === "notes") api.get(`/notes?entity_type=lead&entity_id=${id}`).then((r) => setNotes(r.data));
    if (tab === "audit") api.get(`/audit?entity_type=lead&entity_id=${id}`).then((r) => setAudit(r.data));
    if (tab === "tasks") api.get(`/tasks`).then((r) => setTasks(r.data.filter((t) => t.related_id === id)));
  }, [tab, id]);

  if (!lead) return <div className="p-8 text-slate-400">Loading…</div>;

  const usersById = Object.fromEntries(users.map((u) => [u.id, u]));
  const dirty = Object.keys(pending).length > 0;
  // Merged view = server lead + unsaved pending edits
  const view = { ...lead, ...pending };

  const saveField = (field, value) => {
    setPending((cur) => {
      const next = { ...cur, [field]: value };
      // If the pending value matches the server value, drop it — no unsaved change
      if (JSON.stringify(next[field]) === JSON.stringify(lead[field])) {
        const { [field]: _drop, ...rest } = next;
        return rest;
      }
      return next;
    });
  };

  const saveAll = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${id}`, pending);
      setLead(data);
      setPending({});
      toast.success("Changes saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const discardAll = () => {
    if (!dirty) return;
    setPending({});
    toast.message("Changes discarded");
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/notes?entity_type=lead&entity_id=${id}`, { content: noteText, stage: lead.status });
      setNoteText("");
      const r = await api.get(`/notes?entity_type=lead&entity_id=${id}`);
      setNotes(r.data);
      toast.success("Note added");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8" data-testid="lead-detail-page">
      <Link to="/leads" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"><ChevronLeft className="w-4 h-4" /> Back to leads</Link>

      {dirty && (
        <div className="sticky top-0 z-20 -mx-8 -mt-8 mb-6 px-8 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between" data-testid="lead-save-bar">
          <div className="text-sm text-amber-900">
            <span className="font-semibold">{Object.keys(pending).length}</span> unsaved change{Object.keys(pending).length === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="lead-discard-btn"
              onClick={discardAll}
              disabled={saving}
              className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-md"
            >
              Discard
            </button>
            <button
              type="button"
              data-testid="lead-save-btn"
              onClick={saveAll}
              disabled={saving}
              className="px-4 py-1.5 text-xs font-semibold bg-[#1B4F8B] hover:bg-[#143b67] disabled:opacity-50 text-white rounded-md"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="eyb-label">Lead · {lead.business_name || "—"}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">{lead.lead_name}</h1>
          <div className="flex items-center gap-2 mt-3">
            <Pill className={TEMP_STYLES[lead.temperature]}>{lead.temperature}</Pill>
            <Pill className={STATUS_STYLES[lead.status] || "bg-slate-100"}>{lead.status}</Pill>
            <Pill className="bg-slate-100 text-slate-700 border border-slate-200">{lead.source}</Pill>
            {lead.converted && <Pill className="bg-emerald-50 text-emerald-700 border border-emerald-200">Converted</Pill>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!lead.converted && (
            <button data-testid="convert-lead-btn" onClick={() => setShowConvert(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1B4F8B] hover:bg-[#143b67] text-white text-sm font-semibold">
              Convert to Opportunity <ArrowRight className="w-4 h-4" />
            </button>
          )}
          {lead.converted && lead.opportunity_id && (
            <Link to={`/opportunities/${lead.opportunity_id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1B4F8B] text-white text-sm font-semibold">
              View Opportunity <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          {isAdmin && (
            <button
              data-testid="delete-lead-btn"
              onClick={() => setShowDelete(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm font-semibold"
              title="Delete lead"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="flex border-b border-slate-200 mb-6">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 ${tab === t.id ? "border-[#29ABE2] text-[#187199]" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="eyb-card p-6 lg:col-span-2 space-y-4">
            <h3 className="font-heading font-semibold text-slate-900">Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <EditableField label="Lead Name" value={view.lead_name} onSave={(v) => saveField("lead_name", v)} />
              <EditableField label="Business" value={view.business_name} onSave={(v) => saveField("business_name", v)} />
              <EditableField label="Contact Person" value={view.contact_person} onSave={(v) => saveField("contact_person", v)} />
              <EditableField label="Email" value={view.email} onSave={(v) => saveField("email", v)} />
              <EditableField label="Phone" value={view.phone} onSave={(v) => saveField("phone", v)} />
              <EditableField label="Website" value={view.website} onSave={(v) => saveField("website", v)} />
              <EditableField label="Industry" value={view.industry} onSave={(v) => saveField("industry", v)} />
              <EditableField label="Location" value={view.location} onSave={(v) => saveField("location", v)} />
              <EditableSelect label="Source" value={view.source} options={SOURCES} onSave={(v) => saveField("source", v)} />
              <EditableSelect label="Temperature" value={view.temperature} options={TEMPS} onSave={(v) => saveField("temperature", v)} />
              <EditableSelect label="Status" value={view.status} options={STATUSES} onSave={(v) => saveField("status", v)} />
              <EditableSelect label="Partnership Category" value={view.partnership} options={[{ value: "", label: "— none —" }, ...partnerships.filter((p) => p.active).map((p) => ({ value: p.name, label: p.name }))]} onSave={(v) => { saveField("partnership", v); if (!v) saveField("partner", ""); }} />
              {view.partnership && (
                <EditableSelect
                  label={`Partner Firm (${partnersInCategory.length})`}
                  value={view.partner}
                  options={[{ value: "", label: partnersInCategory.length ? "— select a firm —" : "— add firms in Partnerships —" }, ...partnersInCategory.filter((p) => p.active).map((p) => ({ value: p.name, label: p.name }))]}
                  onSave={(v) => saveField("partner", v)}
                />
              )}
              <EditableSelect
                label="Campaign"
                value={view.campaign_id || ""}
                options={[{ value: "", label: "— none —" }, ...campaigns.map((c) => ({ value: c.id, label: `${c.name}${c.status !== "Active" ? ` (${c.status})` : ""}` }))]}
                onSave={(v) => saveField("campaign_id", v || null)}
              />
            </div>
            <div>
              <div className="eyb-label mb-2">Service Categories</div>
              <div className="flex flex-wrap gap-2">
                {services.filter((s) => s.active).map((s) => {
                  const active = (view.service_categories || []).includes(s.name);
                  return (
                    <button key={s.id} onClick={() => saveField("service_categories", active ? view.service_categories.filter((x) => x !== s.name) : [...(view.service_categories || []), s.name])}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${active ? "bg-[#29ABE2] text-white border-[#29ABE2]" : "bg-white text-slate-600 border-slate-200 hover:border-[#29ABE2]"}`}>
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="eyb-card p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">Ownership</h3>
              <button
                data-testid="transfer-lead-btn"
                onClick={() => setShowTransfer(true)}
                className="text-xs font-semibold text-[#1f8ebf] hover:text-[#187199] inline-flex items-center gap-1"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" /> Transfer
              </button>
            </div>
            <Info label="Created by" value={usersById[lead.created_by]?.name || "—"} />
            <Info label="Assigned to" value={usersById[lead.assigned_to]?.name || "—"} />
            <Info label="Assigned by" value={usersById[lead.assigned_by]?.name || "—"} />
            <Info label="Assigned on" value={fmtDate(lead.assigned_at)} />
            <Info label="Created" value={fmtDateTime(lead.created_at)} />
            <Info label="Last updated" value={fmtDateTime(lead.updated_at)} />
          </div>
        </div>
      )}

      {tab === "overview" && (lead.ownership_history || []).length > 0 && (
        <div className="eyb-card p-6 mt-6" data-testid="ownership-history">
          <div className="flex items-center gap-2 mb-4">
            <ArrowLeftRight className="w-4 h-4 text-slate-400" />
            <h3 className="font-heading font-semibold text-slate-900">Ownership History</h3>
            <span className="text-xs text-slate-400">{lead.ownership_history.length} transfer{lead.ownership_history.length === 1 ? "" : "s"}</span>
          </div>
          <ol className="relative border-l-2 border-slate-100 ml-3 space-y-4">
            {[...lead.ownership_history].reverse().map((h, i) => (
              <li key={i} className="pl-6 relative">
                <span className="absolute -left-2 top-1.5 w-3 h-3 bg-[#29ABE2] rounded-full ring-4 ring-white" />
                <div className="text-sm text-slate-800">
                  <span className="font-semibold">{h.from_user_name}</span>
                  <span className="text-slate-500 mx-1.5">→</span>
                  <span className="font-semibold">{h.to_user_name}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Transferred by {h.by_user_name} · {fmtDateTime(h.at)}
                  {h.notified && <span className="ml-2 text-emerald-600">· Notified by email</span>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {tab === "activity" && <ActivityTab entityType="lead" entityId={id} />}

      {tab === "notes" && (
        <div className="eyb-card p-6">
          <div className="flex gap-2 mb-6">
            <input data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note…" className="flex-1 px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40" />
            <button data-testid="add-note-btn" onClick={addNote} className="px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold rounded-full">Add</button>
          </div>
          <ol className="relative border-l-2 border-slate-100 ml-3 space-y-6">
            {notes.length === 0 && <li className="text-sm text-slate-400 pl-4">No notes yet.</li>}
            {notes.map((n) => (
              <li key={n.id} className="pl-6 relative">
                <span className="absolute -left-2 top-1 w-3 h-3 bg-[#29ABE2] rounded-full ring-4 ring-white" />
                <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.content}</div>
                <div className="text-[11px] text-slate-500 mt-1">{n.created_by_name} · {fmtDateTime(n.created_at)} · Stage: {n.stage || "—"}{!n.active && " · INACTIVE"}</div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {tab === "audit" && (
        <div className="eyb-card p-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="py-2 font-semibold">When</th><th className="py-2 font-semibold">User</th><th className="py-2 font-semibold">Action</th><th className="py-2 font-semibold">Changes</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id} className="border-b border-slate-50">
                  <td className="py-2 text-xs text-slate-500">{fmtDateTime(a.timestamp)}</td>
                  <td className="py-2 text-slate-800">{a.user_name}</td>
                  <td className="py-2 capitalize text-slate-700">{a.action}</td>
                  <td className="py-2 text-xs">
                    {(a.changes || []).map((c, i) => (
                      <div key={i}><span className="font-semibold text-slate-600">{c.field}:</span> <span className="text-rose-500">{c.old_value || "∅"}</span> → <span className="text-emerald-600">{c.new_value}</span></div>
                    ))}
                  </td>
                </tr>
              ))}
              {audit.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400 text-xs">No audit history.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "tasks" && (
        <TasksList relatedType="lead" relatedId={id} tasks={tasks} users={users} onChange={async () => { const r = await api.get("/tasks"); setTasks(r.data.filter((t) => t.related_id === id)); }} />
      )}

      {showConvert && <ConvertModal lead={lead} onClose={() => setShowConvert(false)} onDone={() => { setShowConvert(false); reload(); toast.success("Converted to opportunity"); }} />}

      {showTransfer && <TransferModal entityType="lead" entityId={id} currentOwnerId={lead.assigned_to} users={users} onClose={() => setShowTransfer(false)} onDone={() => { setShowTransfer(false); reload(); }} />}

      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <strong>{lead.lead_name}</strong> and its notes & audit history. This action cannot be undone. Any converted opportunity will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-lead"
              onClick={async () => {
                try {
                  await api.delete(`/leads/${id}`);
                  toast.success("Lead deleted");
                  navigate("/leads");
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Info({ label, value }) {
  return <div className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50"><span className="eyb-label">{label}</span><span className="text-slate-700">{value}</span></div>;
}

function EditableField({ label, value, onSave }) {
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  return (
    <label className="block">
      <span className="eyb-label">{label}</span>
      <input value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== (value || "") && onSave(v)} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40" />
    </label>
  );
}

function EditableSelect({ label, value, options, onSave }) {
  return (
    <label className="block">
      <span className="eyb-label">{label}</span>
      <select value={value || ""} onChange={(e) => onSave(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40">
        <option value="">—</option>
        {options.map((o) => typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function ConvertModal({ lead, onClose, onDone }) {
  const [form, setForm] = useState({ estimated_value: "", expected_close_date: "", stage: "Need Assessment" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const value = Number(form.estimated_value);
    if (!form.estimated_value || isNaN(value) || value <= 0) {
      toast.error("Estimated Deal Value is required and must be greater than 0");
      return;
    }
    if (!form.expected_close_date) {
      toast.error("Expected Close Date is required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/leads/${lead.id}/convert`, { ...form, estimated_value: value });
      onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="convert-modal">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="font-heading text-lg font-semibold">Convert to Opportunity</h3>
          <p className="text-[11px] text-slate-500 mt-1">Partner, campaign and service tags will carry over from this lead.</p>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          <label className="block"><span className="eyb-label">Estimated Deal Value (AUD) <span className="text-rose-500">*</span></span>
            <input data-testid="conv-value" type="number" min="1" step="1" required value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} placeholder="e.g. 5000" className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
          </label>
          <label className="block"><span className="eyb-label">Expected Close Date <span className="text-rose-500">*</span></span>
            <input data-testid="conv-date" type="date" required value={form.expected_close_date} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
          </label>
          <label className="block"><span className="eyb-label">Starting Stage</span>
            <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
            <button data-testid="submit-convert" type="submit" disabled={saving} className="px-4 py-2 bg-[#1B4F8B] hover:bg-[#143b67] text-white text-sm font-semibold rounded-full">{saving ? "Converting…" : "Convert"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TasksList({ relatedType, relatedId, tasks, users, onChange }) {
  const [form, setForm] = useState({ title: "", description: "", due_date: "", priority: "Medium", assigned_to: "" });
  const create = async () => {
    if (!form.title) return;
    try {
      await api.post("/tasks", { ...form, related_type: relatedType, related_id: relatedId });
      setForm({ title: "", description: "", due_date: "", priority: "Medium", assigned_to: "" });
      onChange();
      toast.success("Task created");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setStatus = async (id, status) => { await api.patch(`/tasks/${id}`, { status }); onChange(); };
  return (
    <div className="eyb-card p-6">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-6">
        <input placeholder="Task title…" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="md:col-span-2 px-3 py-2 border border-slate-200 rounded-md text-sm" />
        <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-md text-sm" />
        <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
          {["Low", "Medium", "High"].map((p) => <option key={p}>{p}</option>)}
        </select>
        <select value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
          <option value="">— me —</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <button onClick={create} className="px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold rounded-full">Add Task</button>
      </div>
      <ul className="divide-y divide-slate-100">
        {tasks.map((t) => (
          <li key={t.id} className="py-3 flex items-center justify-between">
            <div>
              <div className="font-medium text-slate-800">{t.title}</div>
              <div className="text-xs text-slate-500">Due {t.due_date || "—"} · {t.priority} · {users.find((u) => u.id === t.assigned_to)?.name || "—"}</div>
            </div>
            <select value={t.status} onChange={(e) => setStatus(t.id, e.target.value)} className="px-2 py-1 border border-slate-200 rounded-md text-xs bg-white">
              {["Open", "In Progress", "Completed"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </li>
        ))}
        {tasks.length === 0 && <li className="text-sm text-slate-400 py-2">No tasks yet.</li>}
      </ul>
    </div>
  );
}
